import React, { useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'recompose';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DeleteConfirmModal } from 'components/FormModal';
import PageHeaderIcon from 'components/PageHeaderIcon';
import SearchInput from 'components/SearchInput';
import { useSearchInput, useUpdateQuery } from 'hooks/useSearchInput';
import DeleteAccount from '../gql/mutations/delete-account';
import GetAccounts from '../gql/queries/get-accounts';

// Page Container
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Header components
const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
`;

// Content wrapper
const ContentWrapper = styled.div`
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header Section
const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const InactiveToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  flex-shrink: 0;
`;

const ToggleCheckbox = styled.input`
  cursor: pointer;
  width: 16px;
  height: 16px;
`;

const InactiveBadge = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(239, 68, 68, 0.15)'
      : 'rgba(239, 68, 68, 0.1)'};
  color: #ef4444;
`;

const AccountsPage = (props) => {
  const { data, deleteAccount } = props;
  const history = useHistory();
  const location = useLocation();
  const { pathname } = location;

  // Context-aware routing
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { input } = useSearchInput();
  const currentPage = input.page ? Number(input.page) : 1;
  const includeInactive = input.includeInactive === 'true';
  const updateQuery = useUpdateQuery();
  const previousSearchRef = useRef(input.queryString || '');

  const accounts = data?.getAccounts?.results ?? [];
  const totalCount = data?.getAccounts?.count ?? 0;
  const loading = data?.loading ?? false;
  const totalPages = Math.ceil(totalCount / 15);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    updateQuery({ page });
  };

  const handleSearch = (searchTerm) => {
    // Only reset to page 1 if the search term actually changed
    if (searchTerm !== previousSearchRef.current) {
      previousSearchRef.current = searchTerm;
      updateQuery({ queryString: searchTerm, page: 1 });
    }
  };

  const handleViewAccount = (account) => {
    history.push(`${routePrefix}/accounts/reports/${account.id}`);
  };

  const handleEditAccount = (account) => {
    history.push(`${routePrefix}/accounts/editAccount/${account.id}`);
  };

  const handleStaffClick = (account) => {
    history.push(`${routePrefix}/accounts/editAccount/${account.id}?tab=staff`);
  };

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;

    setIsDeleting(true);
    try {
      await deleteAccount(accountToDelete.id);
    } catch (error) {
      // Error handling
    } finally {
      setIsDeleting(false);
      setDeleteModalVisible(false);
      setAccountToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setAccountToDelete(null);
  };

  const handleCreateAccount = () => {
    history.push(`${routePrefix}/accounts/addAccount`);
  };

  const handleAnnualsClick = (account) => {
    history.push(`${routePrefix}/accounts/${account.id}/annuals`);
  };

  const handleEventsClick = (account) => {
    history.push(`${routePrefix}/account/${account.id}/events`);
  };

  // Define table columns
  const columns = [
    {
      field: 'business_dba',
      header: 'Name',
      subField: 'legal_name',
      render: (account) => (
        <span>
          {account.business_dba || account.legal_name}
          {account.inactive && <InactiveBadge>Inactive</InactiveBadge>}
        </span>
      ),
    },
    {
      field: 'primary_contact',
      header: 'Primary Contact',
    },
    {
      field: 'legal_name',
      header: 'Properties',
    },
    {
      field: 'events_count',
      header: 'Events',
      render: (account) => account.events_count ?? 0,
    },
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      title: 'View',
      onClick: handleViewAccount,
    },
    {
      type: 'staff',
      title: 'Staff',
      onClick: handleStaffClick,
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      ),
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEditAccount,
    },
    {
      type: 'events',
      title: 'Events',
      onClick: handleEventsClick,
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
          />
        </svg>
      ),
    },
    {
      type: 'calendar',
      title: 'Annuals',
      onClick: handleAnnualsClick,
      icon: (
        <svg
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      ),
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  if (!data?.getAccounts) return null;

  return (
    <EnhancedThemeProvider>
      <div style={{ background: 'transparent', minHeight: '100vh' }}>
        <PageContainer>
          {/* Header Card with Icon and Title */}
          <Card style={{ marginBottom: '30px' }}>
            <HeaderContainer>
              <PageHeaderIcon icon="accounts" />
              <HeaderTitle>Accounts</HeaderTitle>
            </HeaderContainer>
          </Card>

          {/* Main Content Card */}
          <Card noPadding>
            <ContentWrapper>
              <PageHeader>
                <SearchInput
                  onSearch={handleSearch}
                  placeholder="Search accounts by name..."
                />
                <InactiveToggle>
                  <ToggleCheckbox
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) =>
                      updateQuery({
                        includeInactive: e.target.checked ? 'true' : undefined,
                        page: 1,
                      })
                    }
                  />
                  Show Inactive
                </InactiveToggle>
                <ButtonV2
                  onClick={handleCreateAccount}
                  style={{ width: 'auto', flexShrink: 0 }}
                >
                  Create Account
                </ButtonV2>
              </PageHeader>

              <DataTable
                columns={columns}
                data={accounts}
                loading={loading}
                keyField="id"
                emptyMessage="No accounts found"
                emptySubtext="Try adjusting your search terms or create a new account"
                emptyIcon={
                  <svg
                    width="48"
                    height="48"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ marginBottom: '16px', opacity: 0.5 }}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                }
                actions={actions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </ContentWrapper>
          </Card>
        </PageContainer>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalVisible}
        onClose={handleDeleteCancel}
        title="Delete Account"
        message={`Are you sure you want to delete "${
          accountToDelete?.business_dba || accountToDelete?.legal_name
        }"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </EnhancedThemeProvider>
  );
};

export default compose(GetAccounts, DeleteAccount)(AccountsPage);
